import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
  Dimensions,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { PhoneInputComponent } from './design-system/PhoneInput';
import { Input } from './design-system/Input';
import { Button } from './design-system/Button';
import { apiService } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isMobile = screenWidth < 768;

interface AddContactModalProps {
  visible: boolean;
  onClose: () => void;
  onContactAdded?: (contact: { name: string; phoneNumber: string; businessId?: string }) => void;
  businessId?: string;
}

export const AddContactModal: React.FC<AddContactModalProps> = ({
  visible,
  onClose,
  onContactAdded,
  businessId,
}) => {
  const styles = useThemedStyles(getStyles);
  const [contactName, setContactName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const phoneInputRef = React.useRef<any>(null);

  const handleSave = async () => {
    if (!contactName.trim() || !phoneNumber.trim()) {
      Alert.alert('Missing Information', 'Please enter both contact name and phone number.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Get formatted phone number
      let formattedPhoneNumber = phoneNumber;
      if (phoneInputRef.current) {
        const phoneData = phoneInputRef.current.getValue();
        if (phoneData?.formattedNumber) {
          formattedPhoneNumber = phoneData.formattedNumber;
        } else if (phoneData?.dialCode && !phoneNumber.startsWith('+')) {
          formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
        }
      }

      // Ensure phone number starts with +
      if (!formattedPhoneNumber.startsWith('+')) {
        if (phoneInputRef.current) {
          const phoneData = phoneInputRef.current.getValue();
          if (phoneData?.dialCode) {
            formattedPhoneNumber = `${phoneData.dialCode}${phoneNumber}`;
          } else {
            formattedPhoneNumber = `+${phoneNumber}`;
          }
        } else {
          formattedPhoneNumber = `+${phoneNumber}`;
        }
      }

      // Validate phone number - support different country formats
      if (formattedPhoneNumber.length < 8) {
        Alert.alert('Invalid Phone Number', 'Please enter a valid phone number.');
        return;
      }

      // Check if parent contact already exists
      console.log('[AddContactModal] Checking for existing parent contact with phone:', formattedPhoneNumber);
      try {
        const searchResponse = await apiService.searchParentContactsByIdentifiers({
          phone: formattedPhoneNumber,
        });
        
        console.log('[AddContactModal] Search response:', searchResponse);
        
        if (searchResponse.statusCode === 1000 && searchResponse.data && Array.isArray(searchResponse.data) && searchResponse.data.length > 0) {
          // Parent contact already exists - update it if needed
          const existingParent = searchResponse.data[0];
          if (contactName.trim() && contactName.trim() !== existingParent.profile?.name) {
            await apiService.updateParentContact(existingParent._id || existingParent.id, {
              profile: {
                name: contactName.trim(),
              },
            });
          }
          
          // Call the callback with contact data
          if (onContactAdded) {
            onContactAdded({
              name: contactName.trim() || existingParent.profile?.name || formattedPhoneNumber,
              phoneNumber: formattedPhoneNumber,
              businessId: businessId,
            });
          }
          
          // Reset form
          setContactName('');
          setPhoneNumber('');
          
          Alert.alert('Success', 'Contact added successfully!');
          onClose();
          return;
        }
      } catch (error) {
        console.error('Error checking for existing parent contact:', error);
      }
      
      // Create new parent contact
      // Note: We don't include channels array for manual creation since we don't have channelContactId yet
      // The channel will be linked automatically when the contact interacts through WhatsApp
      const contactData = {
        primaryIdentifier: formattedPhoneNumber,
        primaryIdentifierType: 'phone',
        identifiers: {
          phone: [formattedPhoneNumber], // Backend expects arrays
          whatsapp: [formattedPhoneNumber], // Backend expects arrays
        },
        profile: {
          name: contactName.trim() || formattedPhoneNumber,
          phone: formattedPhoneNumber,
        },
        // Don't include channels - channelContactId is required but we don't have it for manual creation
        // The channel will be linked when the contact actually interacts through WhatsApp
      };
      console.log('[AddContactModal] Creating parent contact with data:', contactData);
      
      try {
        const createResponse = await apiService.createParentContact(contactData);
        
        console.log('[AddContactModal] Create parent contact response:', createResponse);
        
        if (createResponse.statusCode === 1000 && createResponse.data) {
          console.log('[AddContactModal] ✅ Parent contact created successfully:', createResponse.data);
          // Call the callback with contact data
          if (onContactAdded) {
            onContactAdded({
              name: contactName.trim() || formattedPhoneNumber,
              phoneNumber: formattedPhoneNumber,
              businessId: businessId,
            });
          }
          
          // Reset form
          setContactName('');
          setPhoneNumber('');
          
          Alert.alert('Success', 'Contact added successfully!');
          onClose();
        } else {
          const errorMsg = createResponse.errors?.[0]?.message || createResponse.message || 'Failed to create contact';
          console.error('[AddContactModal] ❌ Failed to create parent contact:', errorMsg, createResponse);
          throw new Error(errorMsg);
        }
      } catch (error: any) {
        console.error('[AddContactModal] ❌ Error creating parent contact:', error);
        console.error('[AddContactModal] Error details:', {
          message: error.message,
          response: error.response,
          statusCode: error.statusCode,
          errors: error.errors,
        });
        
        // Show error to user instead of silently falling back
        const errorMessage = error.message || error.response?.data?.message || 'Failed to create parent contact. Please check the console for details.';
        console.error('[AddContactModal] Full error object:', JSON.stringify(error, null, 2));
        Alert.alert(
          'Error Creating Contact',
          errorMessage,
          [
            {
              text: 'OK',
              onPress: () => {
                // Don't fallback - let user try again or check what went wrong
              }
            }
          ]
        );
        
        // Don't fallback to old API - we want to use parent contacts
        // Re-throw to be caught by outer catch
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('[AddContactModal] ❌ Error in handleSave:', error);
      const errorMessage = error.message || 'Failed to add contact. Please try again.';
      console.error('[AddContactModal] Error message:', errorMessage);
      Alert.alert('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setContactName('');
    setPhoneNumber('');
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <View style={styles.iconContainer}>
                <Ionicons name="person-add" size={24} color={colors.primary} />
              </View>
              <Text style={styles.title}>Add New Contact</Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text.secondary} />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            <View style={styles.formContainer}>
              <Input
                label="Contact Name"
                value={contactName}
                onChangeText={setContactName}
                placeholder="Enter contact name"
                autoCapitalize="words"
                required
                error={!contactName.trim() && isLoading ? 'Contact name is required' : undefined}
              />

              <View style={styles.phoneInputSection}>
                <Text style={styles.phoneLabel}>Phone Number *</Text>
                <PhoneInputComponent
                  ref={phoneInputRef}
                  value={phoneNumber}
                  onChangeText={setPhoneNumber}
                  onChange={setPhoneNumber}
                  placeholder="Enter phone number"
                  style={styles.phoneInput}
                />
                {!phoneNumber.trim() && isLoading && (
                  <Text style={styles.errorText}>Phone number is required</Text>
                )}
              </View>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <Button
              variant="outlined"
              onPress={handleClose}
              disabled={isLoading}
              style={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleSave}
              disabled={isLoading || !contactName.trim() || !phoneNumber.trim()}
              loading={isLoading}
              style={styles.saveButton}
            >
              Add Contact
            </Button>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  modalContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    width: isMobile ? '95%' : '85%',
    maxWidth: isMobile ? screenWidth - (spacing.lg * 2) : 500,
    maxHeight: '85%',
    elevation: 8,
    shadowColor: colors.text.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primaryTransparent,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    flex: 1,
  },
  closeButton: {
    padding: spacing.sm,
    marginLeft: spacing.sm,
  },
  content: {
    padding: spacing.lg,
  },
  formContainer: {
    gap: spacing.lg,
  },
  phoneInputSection: {
    marginBottom: spacing.sm,
  },
  phoneLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  phoneInput: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    marginTop: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.md,
  },
  cancelButton: {
    flex: 1,
  },
  saveButton: {
    flex: 1,
  },
});
